import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box, Typography, Button, Chip, CircularProgress, Alert, Divider,
  TextField, InputAdornment, IconButton, Menu, MenuItem, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

const Z_PURPLE = "#38184f";
const Z_BLUE = "#2baede";
// Days past a payment/credit date before it's flagged overdue. Mirrors the
// OVERDUE_DAYS constant in the bankReconV1 service — keep the two in sync.
const OVERDUE_DAYS = 14;

const AUD = (v) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(v ?? 0);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// Raw fetch helper for multipart file uploads (phappsRequest only does JSON)
async function uploadFile(endpoint, formData) {
  const token = localStorage.getItem("jwtToken");
  const urlBase = import.meta.env.VITE_APP_DATA_API_URL;
  const res = await fetch(`${urlBase}${endpoint}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Upload failed (${res.status})`);
  }
  return res.json();
}

const STRENGTH_COLOR = { strong: "#2e7d32", medium: "#e65100", weak: "#757575" };
const STRENGTH_BG    = { strong: "#e8f5e9", medium: "#fff3e0", weak: "#f5f5f5" };

function StrengthPill({ strength, hint }) {
  if (!strength) return null;
  return (
    <Tooltip title={hint || ""} placement="left">
      <Chip
        label={strength}
        size="small"
        sx={{
          fontSize: "0.68rem",
          fontWeight: 700,
          color: STRENGTH_COLOR[strength],
          bgcolor: STRENGTH_BG[strength],
          border: `1px solid ${STRENGTH_COLOR[strength]}40`,
          height: 20,
          ml: 1,
        }}
      />
    </Tooltip>
  );
}

function KebabMenu({ items }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        sx={{ opacity: 0.5, "&:hover": { opacity: 1 } }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {items.map((item) => (
          <MenuItem key={item.label} onClick={() => { setAnchor(null); item.action(); }}
            sx={{ fontSize: "0.85rem", color: item.danger ? "#c62828" : "inherit" }}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

// Small age badge shown on each unmatched row. Turns red once overdue.
function AgePill({ ageDays }) {
  if (ageDays == null) return null;
  const overdue = ageDays >= OVERDUE_DAYS;
  return (
    <Chip
      label={overdue ? `${ageDays}d overdue` : `${ageDays}d`}
      size="small"
      sx={{
        height: 18, fontSize: "0.65rem", fontWeight: 700, ml: 1,
        color: overdue ? "#c62828" : "#999",
        bgcolor: overdue ? "#ffebee" : "#f2f2f2",
        border: overdue ? "1px solid #c6282840" : "none",
        "& .MuiChip-label": { px: 0.75 },
      }}
    />
  );
}

function StatCard({ label, count, value, color, overdueCount, overdueValue, onOverdueClick }) {
  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: 2, px: 2.5, py: 1.5, minWidth: 160, borderTop: `3px solid ${color}` }}>
      <Typography sx={{ fontSize: "0.75rem", color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 }}>
        {count}
      </Typography>
      <Typography sx={{ fontSize: "0.85rem", color: "#555" }}>{AUD(value)}</Typography>
      {overdueCount > 0 && (
        <Chip
          label={`${overdueCount} overdue · ${AUD(overdueValue)}`}
          size="small"
          onClick={onOverdueClick}
          sx={{
            mt: 0.75, height: 20, fontSize: "0.68rem", fontWeight: 700,
            color: "#c62828", bgcolor: "#ffebee", border: "1px solid #c6282840",
            cursor: onOverdueClick ? "pointer" : "default",
          }}
        />
      )}
    </Box>
  );
}

// Review dialog for "probably correct" pairs the auto-matcher won't commit on
// its own (usually because the payment and bank credit dates are weeks apart).
// All rows are pre-ticked; confirming commits the ticked ones as real matches.
const SUGGEST_TIER_STYLE = {
  suggested_alias:    { color: "#2e7d32", bg: "#e8f5e9", label: "alias" },
  suggested_banktext: { color: "#1565c0", bg: "#e3f2fd", label: "bank text" },
  suggested_amount:   { color: "#e65100", bg: "#fff3e0", label: "amount only" },
};

function SuggestedMatchesDialog({ open, suggestions, confirming, onClose, onConfirm }) {
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (open) setSelected(new Set(suggestions.map((s) => s.TransID)));
  }, [open, suggestions]);

  const toggle = (transID) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(transID)) next.delete(transID); else next.add(transID);
      return next;
    });

  const chosen = suggestions.filter((s) => selected.has(s.TransID));

  return (
    <Dialog open={open} onClose={() => !confirming && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700, pb: 0.5 }}>
        Probable matches — please confirm
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "0.82rem", color: "#666", mb: 1.5 }}>
          These pairs match on amount and payer text (or are the only candidate), but the dates
          are too far apart for auto-match to commit them safely. Untick any that look wrong.
        </Typography>
        {suggestions.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center", color: "#aaa", fontSize: "0.85rem" }}>
            No probable matches right now
          </Box>
        ) : suggestions.map((s) => {
          const tier = SUGGEST_TIER_STYLE[s.Tier] || SUGGEST_TIER_STYLE.suggested_amount;
          return (
            <Box
              key={s.TransID}
              onClick={() => toggle(s.TransID)}
              sx={{
                display: "flex", alignItems: "center", gap: 1, px: 1, py: 1,
                borderBottom: "1px solid #f0f0f0", cursor: "pointer", borderRadius: 1,
                bgcolor: selected.has(s.TransID) ? `${Z_BLUE}08` : "transparent",
                "&:hover": { bgcolor: `${Z_BLUE}10` },
              }}
            >
              <Checkbox size="small" checked={selected.has(s.TransID)} sx={{ p: 0.5 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#333" }}>
                  {s.PayerName || "No payer linked"} · {fmtDate(s.PayDate)}
                  {s.ReferenceNumber ? ` · ${s.ReferenceNumber}` : ""}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  → {(s.TransText ?? "").replace(/^transfer from\s*/i, "")} · {fmtDate(s.TransDate)}
                </Typography>
              </Box>
              <Tooltip title={s.Reason} placement="left">
                <Chip
                  label={`${tier.label} · ${s.DaysApart}d apart`}
                  size="small"
                  sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, color: tier.color, bgcolor: tier.bg, flexShrink: 0 }}
                />
              </Tooltip>
              <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#1a1a1a", ml: 0.5, flexShrink: 0, minWidth: 80, textAlign: "right" }}>
                {AUD(parseFloat(s.TransAmount))}
              </Typography>
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={confirming} sx={{ color: "#888", fontSize: "0.82rem" }}>
          Not now
        </Button>
        <Button
          variant="contained"
          disabled={confirming || chosen.length === 0}
          onClick={() => onConfirm(chosen.map((s) => ({ transID: s.TransID, paymentID: s.PaymentID })))}
          startIcon={confirming ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
          sx={{ bgcolor: Z_PURPLE, "&:hover": { bgcolor: "#2d1040" }, fontSize: "0.82rem", fontWeight: 700 }}
        >
          {confirming ? "Confirming…" : `Confirm ${chosen.length} match${chosen.length === 1 ? "" : "es"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ZephyrBankReconPage() {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [selectedTrans, setSelectedTrans] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [recentlyMatched, setRecentlyMatched] = useState([]);
  const [showRecent, setShowRecent] = useState(false);
  const [aliasPrompt, setAliasPrompt] = useState(null);
  const [editedAlias, setEditedAlias] = useState("");
  const [transSearch, setTransSearch] = useState("");
  const [paymentSearch, setPaymentSearch] = useState("");
  const [view, setView] = useState("reconcile"); // 'reconcile' | 'ignored'
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [ignoredTrans, setIgnoredTrans] = useState([]);
  const [ignoredPayments, setIgnoredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [matching, setMatching] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [suggested, setSuggested] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [confirmingSuggested, setConfirmingSuggested] = useState(false);
  const [bulkPaySelected, setBulkPaySelected] = useState(new Set());
  const [bulkIgnoring, setBulkIgnoring] = useState(false);
  const fileInputRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [sumRes, transRes, payRes, recentRes, suggRes] = await Promise.all([
        phappsRequest(API_PATHS.ZEPHYR_RECON_SUMMARY, "GET"),
        phappsRequest(API_PATHS.ZEPHYR_RECON_TRANSACTIONS, "GET"),
        phappsRequest(API_PATHS.ZEPHYR_RECON_PAYMENTS, "GET"),
        phappsRequest(API_PATHS.ZEPHYR_RECON_RECENTLY_MATCHED, "GET"),
        phappsRequest(API_PATHS.ZEPHYR_RECON_SUGGESTED_MATCHES, "GET"),
      ]);
      setSummary(sumRes?.data ?? null);
      setTransactions(transRes?.data ?? []);
      setPayments(payRes?.data ?? []);
      setRecentlyMatched(recentRes?.data ?? []);
      const sugg = suggRes?.data ?? [];
      setSuggested(sugg);
      setBulkPaySelected(new Set());
      return sugg;
    } catch (e) {
      setError(e.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadIgnored = useCallback(async () => {
    try {
      const [t, p] = await Promise.all([
        phappsRequest(API_PATHS.ZEPHYR_RECON_IGNORED_TRANS, "GET"),
        phappsRequest(API_PATHS.ZEPHYR_RECON_IGNORED_PAYMENTS, "GET"),
      ]);
      setIgnoredTrans(t?.data ?? []);
      setIgnoredPayments(p?.data ?? []);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { if (view === "ignored") loadIgnored(); }, [view, loadIgnored]);

  const isOverdue = (ageDays) => ageDays != null && ageDays >= OVERDUE_DAYS;

  // Left column: payments — filtered by search + overdue toggle
  const displayPayments = payments.filter((p) =>
    (!overdueOnly || isOverdue(p.ageDays)) &&
    (!paymentSearch || (p.PayerName || "").toLowerCase().includes(paymentSearch.toLowerCase()) || (p.ReferenceNumber || "").toLowerCase().includes(paymentSearch.toLowerCase()))
  );

  // Right column: when a payment is selected, highlight bank credits matching by amount
  const transHints = selectedPayment
    ? transactions.filter((t) => Math.abs(t.TransAmount - parseFloat(selectedPayment.remaining_amount ?? selectedPayment.TotalPaymentValue)) < 0.005)
    : null;

  const filteredTrans = transactions.filter((t) =>
    (!overdueOnly || isOverdue(t.ageDays)) &&
    (!transSearch || t.TransText?.toLowerCase().includes(transSearch.toLowerCase()))
  );
  const displayTrans = transHints !== null ? transHints : filteredTrans;

  const handleSelectPayment = (payment) => {
    setSelectedTrans(null);
    setSuggestions(null);
    if (selectedPayment?.PaymentID === payment.PaymentID) {
      setSelectedPayment(null);
      return;
    }
    setSelectedPayment(payment);
  };

  const handleSelectTrans = (trans) => {
    setSelectedTrans((prev) =>
      prev?.TransID === trans.TransID ? null : trans
    );
  };

  const handleMatch = async () => {
    if (!selectedTrans || !selectedPayment) return;
    setMatching(true);
    try {
      const res = await phappsRequest(API_PATHS.ZEPHYR_RECON_MATCH, "POST", {
        transID: selectedTrans.TransID,
        paymentID: selectedPayment.PaymentID,
      });
      setSelectedTrans(null);
      setSelectedPayment(null);
      setSuggestions(null);
      if (res?.data?.suggestedAlias) {
        setAliasPrompt(res.data.suggestedAlias);
        setEditedAlias(res.data.suggestedAlias.aliasText);
      }
      await loadAll();
    } catch (e) {
      setError(e.message);
    } finally {
      setMatching(false);
    }
  };

  const handleSaveAlias = async (save) => {
    if (save && aliasPrompt) {
      try {
        await phappsRequest(API_PATHS.ZEPHYR_RECON_ALIAS, "POST", {
          payerType: aliasPrompt.payerType,
          payerID: aliasPrompt.payerID,
          aliasText: editedAlias,
        });
      } catch {
        // non-fatal
      }
    }
    setAliasPrompt(null);
    setEditedAlias("");
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setBatchResult(null);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await uploadFile(API_PATHS.ZEPHYR_RECON_UPLOAD, fd);
      setBatchResult(res?.data ?? null);
      const sugg = await loadAll();
      if (sugg.length > 0) setSuggestOpen(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleAutoMatch = async () => {
    setAutoRunning(true);
    setError(null);
    try {
      const res = await phappsRequest(API_PATHS.ZEPHYR_RECON_AUTO_MATCH, "POST");
      const sugg = await loadAll();
      if (sugg.length > 0) {
        setSuggestOpen(true);
      } else {
        setNotice(`Auto-match complete — ${res?.data?.total ?? 0} matched automatically, no further probable matches found.`);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setAutoRunning(false);
    }
  };

  const handleConfirmSuggested = async (pairs) => {
    setConfirmingSuggested(true);
    setError(null);
    try {
      const res = await phappsRequest(API_PATHS.ZEPHYR_RECON_CONFIRM_SUGGESTED, "POST", { pairs });
      setSuggestOpen(false);
      const confirmed = res?.data?.confirmed ?? 0;
      const skipped = res?.data?.skipped ?? 0;
      setNotice(
        `${confirmed} match${confirmed === 1 ? "" : "es"} confirmed` +
        (skipped ? `, ${skipped} skipped (already matched or changed since suggested)` : "") + "."
      );
      await loadAll();
    } catch (e) {
      setError(e.message);
    } finally {
      setConfirmingSuggested(false);
    }
  };

  const toggleBulkPayment = (paymentID) =>
    setBulkPaySelected((prev) => {
      const next = new Set(prev);
      if (next.has(paymentID)) next.delete(paymentID); else next.add(paymentID);
      return next;
    });

  const handleBulkIgnorePayments = async () => {
    const ids = [...bulkPaySelected];
    if (!ids.length) return;
    setBulkIgnoring(true);
    setError(null);
    try {
      await phappsRequest(API_PATHS.ZEPHYR_RECON_BULK_IGNORE_PAYMENTS, "POST", { paymentIDs: ids });
      setPayments((prev) => prev.filter((p) => !bulkPaySelected.has(p.PaymentID)));
      if (selectedPayment && bulkPaySelected.has(selectedPayment.PaymentID)) setSelectedPayment(null);
      setBulkPaySelected(new Set());
      setNotice(`${ids.length} payment${ids.length === 1 ? "" : "s"} moved to Ignored.`);
      await phappsRequest(API_PATHS.ZEPHYR_RECON_SUMMARY, "GET").then((r) => setSummary(r?.data));
    } catch (e) {
      setError(e.message);
    } finally {
      setBulkIgnoring(false);
    }
  };

  const handleIgnoreTrans = async (transID) => {
    try {
      await phappsRequest(API_PATHS.ZEPHYR_RECON_IGNORE_TRANS, "POST", { transID });
      setTransactions((prev) => prev.filter((t) => t.TransID !== transID));
      if (selectedTrans?.TransID === transID) { setSelectedTrans(null); setSuggestions(null); }
      await phappsRequest(API_PATHS.ZEPHYR_RECON_SUMMARY, "GET").then((r) => setSummary(r?.data));
    } catch (e) { setError(e.message); }
  };

  const handleIgnorePayment = async (paymentID) => {
    try {
      await phappsRequest(API_PATHS.ZEPHYR_RECON_IGNORE_PAYMENT, "POST", { paymentID });
      setPayments((prev) => prev.filter((p) => p.PaymentID !== paymentID));
      setSuggestions((prev) => prev ? prev.filter((p) => p.PaymentID !== paymentID) : null);
      if (selectedPayment?.PaymentID === paymentID) setSelectedPayment(null);
      await phappsRequest(API_PATHS.ZEPHYR_RECON_SUMMARY, "GET").then((r) => setSummary(r?.data));
    } catch (e) { setError(e.message); }
  };

  const handleUnmatch = async (transID) => {
    try {
      await phappsRequest(API_PATHS.ZEPHYR_RECON_UNMATCH, "POST", { transID });
      await loadAll();
    } catch (e) { setError(e.message); }
  };

  const handleUnignoreTrans = async (transID) => {
    try {
      await phappsRequest(API_PATHS.ZEPHYR_RECON_UNIGNORE_TRANS, "POST", { transID });
      setIgnoredTrans((prev) => prev.filter((t) => t.TransID !== transID));
      await loadAll();
    } catch (e) { setError(e.message); }
  };

  const handleUnignorePayment = async (paymentID) => {
    try {
      await phappsRequest(API_PATHS.ZEPHYR_RECON_UNIGNORE_PAYMENT, "POST", { paymentID });
      setIgnoredPayments((prev) => prev.filter((p) => p.PaymentID !== paymentID));
      await loadAll();
    } catch (e) { setError(e.message); }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", p: 4 }}>
        <CircularProgress sx={{ color: Z_PURPLE }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", bgcolor: "#f4f5f7" }}>

      {/* ── Header ──────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: Z_PURPLE, px: 3, py: 1.5, display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem" }}>
          Bank Reconciliation
        </Typography>

        {/* View switcher — Reconcile / Ignored */}
        <Box sx={{ display: "flex", bgcolor: "rgba(255,255,255,0.1)", borderRadius: 1.5, p: 0.4, ml: 1 }}>
          {[
            { key: "reconcile", label: "Reconcile" },
            { key: "ignored", label: "Ignored" },
          ].map((v) => (
            <Box
              key={v.key}
              onClick={() => setView(v.key)}
              sx={{
                px: 1.75, py: 0.5, borderRadius: 1, cursor: "pointer", fontSize: "0.78rem", fontWeight: 700,
                color: view === v.key ? Z_PURPLE : "rgba(255,255,255,0.75)",
                bgcolor: view === v.key ? "#fff" : "transparent",
                transition: "all 0.12s",
              }}
            >
              {v.label}
            </Box>
          ))}
        </Box>

        <Box sx={{ flex: 1 }} />

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ""; }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <UploadFileIcon />}
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.5)", "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.08)" }, fontSize: "0.8rem" }}
        >
          {uploading ? "Uploading…" : "Import CSV"}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={autoRunning ? <CircularProgress size={14} color="inherit" /> : <AutoFixHighIcon />}
          disabled={autoRunning}
          onClick={handleAutoMatch}
          sx={{ color: Z_BLUE, borderColor: `${Z_BLUE}80`, "&:hover": { borderColor: Z_BLUE, bgcolor: `${Z_BLUE}15` }, fontSize: "0.8rem" }}
        >
          {autoRunning ? "Running…" : "Run Auto-Match"}
        </Button>
      </Box>

      {/* ── Summary strip ────────────────────────────────────────────── */}
      {view === "reconcile" && (
      <Box sx={{ px: 3, py: 1.5, display: "flex", gap: 2, flexShrink: 0, flexWrap: "wrap", alignItems: "center" }}>
        <StatCard
          label="Unmatched Bank Credits" color={Z_BLUE}
          count={summary?.unmatchedTransactions ?? 0} value={summary?.unmatchedTransactionsValue}
          overdueCount={summary?.overdueTransactions} overdueValue={summary?.overdueTransactionsValue}
          onOverdueClick={() => setOverdueOnly(true)}
        />
        <StatCard
          label="Unmatched Payments" color={Z_PURPLE}
          count={summary?.unmatchedPayments ?? 0} value={summary?.unmatchedPaymentsValue}
          overdueCount={summary?.overduePayments} overdueValue={summary?.overduePaymentsValue}
          onOverdueClick={() => setOverdueOnly(true)}
        />
        {suggested.length > 0 && (
          <Chip
            label={`${suggested.length} probable match${suggested.length === 1 ? "" : "es"} — Review`}
            onClick={() => setSuggestOpen(true)}
            sx={{
              fontSize: "0.75rem", fontWeight: 700, height: 30, alignSelf: "center",
              color: "#7a5200", bgcolor: "#fff3cd", border: "1px solid #e0a80080",
              "&:hover": { bgcolor: "#ffe9a8" },
            }}
          />
        )}
        <Chip
          label={overdueOnly ? `Overdue only (>${OVERDUE_DAYS}d)` : "Show all ages"}
          onClick={() => setOverdueOnly((v) => !v)}
          variant={overdueOnly ? "filled" : "outlined"}
          sx={{
            fontSize: "0.75rem", fontWeight: 700, height: 30, alignSelf: "center",
            color: overdueOnly ? "#fff" : "#c62828",
            bgcolor: overdueOnly ? "#c62828" : "transparent",
            borderColor: "#c6282880",
            "&:hover": { bgcolor: overdueOnly ? "#b71c1c" : "#ffebee" },
          }}
        />
        {summary?.lastBatch && (
          <Box sx={{ bgcolor: "#fff", borderRadius: 2, px: 2, py: 1.5, fontSize: "0.8rem", color: "#555", ml: "auto" }}>
            <Typography sx={{ fontSize: "0.72rem", color: "#888", fontWeight: 600, textTransform: "uppercase" }}>Last Import</Typography>
            <Typography sx={{ fontSize: "0.88rem", color: "#333", fontWeight: 600 }}>{summary.lastBatch.Filename}</Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "#666" }}>
              {fmtDate(summary.lastBatch.UploadedAt)} &middot; {summary.lastBatch.RowsInserted} rows &middot; {summary.lastBatch.AutoMatched} auto-matched
            </Typography>
          </Box>
        )}
      </Box>
      )}

      {/* ── Batch result ─────────────────────────────────────────────── */}
      {batchResult && (
        <Box sx={{ px: 3, pb: 1, flexShrink: 0 }}>
          <Alert severity="success" onClose={() => setBatchResult(null)} sx={{ fontSize: "0.82rem" }}>
            Import complete — {batchResult.RowsInserted} inserted, {batchResult.RowsSkipped} skipped (duplicates), {batchResult.AutoMatched} auto-matched.
            {batchResult.ParseErrors > 0 && ` (${batchResult.ParseErrors} lines could not be parsed)`}
          </Alert>
        </Box>
      )}

      {notice && (
        <Box sx={{ px: 3, pb: 1, flexShrink: 0 }}>
          <Alert severity="success" onClose={() => setNotice(null)} sx={{ fontSize: "0.82rem" }}>{notice}</Alert>
        </Box>
      )}

      {error && (
        <Box sx={{ px: 3, pb: 1, flexShrink: 0 }}>
          <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
        </Box>
      )}

      {/* ── Reconcile view ───────────────────────────────────────────── */}
      {view === "reconcile" && (<>
      {/* ── Two-column main area ─────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: "flex", gap: 2, px: 3, pb: 2, overflow: "hidden", minHeight: 0 }}>

        {/* ── Left: payments (primary selection) ── */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", bgcolor: "#fff", borderRadius: 2, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <Box sx={{ px: 2, pt: 2, pb: 1, borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, minHeight: 30 }}>
              <Checkbox
                size="small"
                sx={{ p: 0.5, ml: -0.5 }}
                checked={displayPayments.length > 0 && displayPayments.every((p) => bulkPaySelected.has(p.PaymentID))}
                indeterminate={bulkPaySelected.size > 0 && !displayPayments.every((p) => bulkPaySelected.has(p.PaymentID))}
                onChange={(e) =>
                  setBulkPaySelected(e.target.checked
                    ? new Set(displayPayments.map((p) => p.PaymentID))
                    : new Set())
                }
              />
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: Z_PURPLE }}>
                Unmatched Payments ({displayPayments.length})
              </Typography>
              <Box sx={{ flex: 1 }} />
              {bulkPaySelected.size > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  disabled={bulkIgnoring}
                  startIcon={bulkIgnoring ? <CircularProgress size={12} color="inherit" /> : <VisibilityOffIcon sx={{ fontSize: 15 }} />}
                  onClick={handleBulkIgnorePayments}
                  sx={{ fontSize: "0.72rem", fontWeight: 700, py: 0.25 }}
                >
                  Ignore selected ({bulkPaySelected.size})
                </Button>
              )}
            </Box>
            <TextField
              size="small" placeholder="Search payments…" fullWidth
              value={paymentSearch} onChange={(e) => setPaymentSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "#aaa" }} /></InputAdornment> }}
              sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.82rem" } }}
            />
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {displayPayments.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center", color: "#aaa", fontSize: "0.85rem" }}>
                No unmatched payments
              </Box>
            ) : displayPayments.map((p) => {
              const isSelected = selectedPayment?.PaymentID === p.PaymentID;
              const remaining = parseFloat(p.remaining_amount ?? p.TotalPaymentValue);
              const isPartial = parseFloat(p.matched_total) > 0;
              return (
                <Box
                  key={p.PaymentID}
                  onClick={() => handleSelectPayment(p)}
                  sx={{
                    display: "flex", alignItems: "center", px: 2, py: 1.25,
                    cursor: "pointer", borderBottom: "1px solid #f5f5f5",
                    bgcolor: isSelected ? `${Z_PURPLE}10` : "transparent",
                    borderLeft: isSelected ? `3px solid ${Z_PURPLE}` : "3px solid transparent",
                    "&:hover": { bgcolor: isSelected ? `${Z_PURPLE}18` : "#fafafa" },
                    transition: "background 0.1s",
                  }}
                >
                  <Box onClick={(e) => e.stopPropagation()} sx={{ mr: 0.5, ml: -1 }}>
                    <Checkbox
                      size="small"
                      sx={{ p: 0.5 }}
                      checked={bulkPaySelected.has(p.PaymentID)}
                      onChange={() => toggleBulkPayment(p.PaymentID)}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography sx={{ fontSize: "0.82rem", color: "#333", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p.PayerName || <span style={{ color: "#aaa", fontStyle: "italic" }}>No payer linked</span>}
                      </Typography>
                      <AgePill ageDays={p.ageDays} />
                    </Box>
                    <Typography sx={{ fontSize: "0.75rem", color: "#888", mt: 0.25 }}>
                      {fmtDate(p.PayDate)}
                      {p.ReferenceNumber ? ` · ${p.ReferenceNumber}` : ""}
                      {isPartial && ` · ${AUD(parseFloat(p.matched_total))} matched`}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right", flexShrink: 0, ml: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a1a" }}>
                      {AUD(remaining)}
                    </Typography>
                    {isPartial && (
                      <Typography sx={{ fontSize: "0.7rem", color: "#888" }}>
                        of {AUD(p.TotalPaymentValue)}
                      </Typography>
                    )}
                  </Box>
                  <Box onClick={(e) => e.stopPropagation()} sx={{ ml: 0.5 }}>
                    <KebabMenu items={[
                      { label: "Ignore for reconciliation", danger: true, action: () => handleIgnorePayment(p.PaymentID) },
                    ]} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ── Right: bank credits (secondary selection) ── */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", bgcolor: "#fff", borderRadius: 2, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <Box sx={{ px: 2, pt: 2, pb: 1, borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: Z_PURPLE, mb: 1 }}>
              {transHints !== null
                ? `Matching Bank Credits for ${AUD(parseFloat(selectedPayment?.remaining_amount ?? selectedPayment?.TotalPaymentValue))} (${transHints.length})`
                : `Bank Credits (${filteredTrans.length})`}
            </Typography>
            <TextField
              size="small" placeholder="Search transactions…" fullWidth
              value={transSearch} onChange={(e) => setTransSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "#aaa" }} /></InputAdornment> }}
              sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.82rem" } }}
            />
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {displayTrans.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center", color: "#aaa", fontSize: "0.85rem" }}>
                {transHints !== null ? "No bank credits match this amount" : "No unmatched bank transactions"}
              </Box>
            ) : displayTrans.map((t) => {
              const isSelected = selectedTrans?.TransID === t.TransID;
              const isHint = transHints !== null;
              return (
                <Box
                  key={t.TransID}
                  onClick={() => handleSelectTrans(t)}
                  sx={{
                    display: "flex", alignItems: "center", px: 2, py: 1.25,
                    cursor: "pointer", borderBottom: "1px solid #f5f5f5",
                    bgcolor: isSelected ? `${Z_BLUE}12` : isHint ? `${Z_BLUE}06` : "transparent",
                    borderLeft: isSelected ? `3px solid ${Z_BLUE}` : "3px solid transparent",
                    "&:hover": { bgcolor: isSelected ? `${Z_BLUE}18` : "#fafafa" },
                    transition: "background 0.1s",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                      <Typography sx={{ fontSize: "0.82rem", color: "#333", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {(t.TransText ?? "").replace(/^transfer from\s*/i, "")}
                      </Typography>
                      <AgePill ageDays={t.ageDays} />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
                      <Typography sx={{ fontSize: "0.75rem", color: "#888" }}>
                        {fmtDate(t.TransDate)}
                      </Typography>
                      {t.likelyPayerName && (
                        <Tooltip title={`Inferred from ${t.likelyPayerSource === "alias" ? "a learned alias" : "configured bank text"} — no remittance matched yet`} placement="top">
                          <Chip
                            label={`likely ${t.likelyPayerName}`}
                            size="small"
                            sx={{ height: 18, fontSize: "0.65rem", fontWeight: 600, color: Z_PURPLE, bgcolor: `${Z_PURPLE}12`, "& .MuiChip-label": { px: 0.75 } }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a1a", ml: 1, flexShrink: 0 }}>
                    {AUD(t.TransAmount)}
                  </Typography>
                  <Box onClick={(e) => e.stopPropagation()} sx={{ ml: 0.5 }}>
                    <KebabMenu items={[
                      { label: "Ignore this transaction", danger: true, action: () => handleIgnoreTrans(t.TransID) },
                    ]} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* ── Match bar ────────────────────────────────────────────────── */}
      {selectedTrans && selectedPayment && (
        <Box sx={{ px: 3, pb: 2, flexShrink: 0 }}>
          <Box sx={{ bgcolor: Z_PURPLE, borderRadius: 2, px: 2.5, py: 1.5, display: "flex", alignItems: "center", gap: 2 }}>
            <CheckCircleOutlineIcon sx={{ color: Z_BLUE, fontSize: 20 }} />
            <Typography sx={{ color: "#fff", fontSize: "0.85rem", flex: 1 }}>
              Match Payment #{selectedPayment.PaymentID} — <strong>{selectedPayment.PayerName}</strong>{" "}
              {AUD(parseFloat(selectedPayment.remaining_amount ?? selectedPayment.TotalPaymentValue))} ({fmtDate(selectedPayment.PayDate)})
              {" → "}
              <strong>{(selectedTrans.TransText ?? "").replace(/^transfer from\s*/i, "")}</strong>{" "}
              {AUD(selectedTrans.TransAmount)} on {fmtDate(selectedTrans.TransDate)}
            </Typography>
            <Button
              variant="contained"
              size="small"
              disabled={matching}
              onClick={handleMatch}
              sx={{ bgcolor: Z_BLUE, "&:hover": { bgcolor: "#1a9dc7" }, fontWeight: 700, flexShrink: 0 }}
            >
              {matching ? <CircularProgress size={14} color="inherit" /> : "Confirm Match"}
            </Button>
            <Button size="small" onClick={() => { setSelectedTrans(null); setSelectedPayment(null); setSuggestions(null); }}
              sx={{ color: "rgba(255,255,255,0.6)", minWidth: 0 }}>
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Recently matched ─────────────────────────────────────────── */}
      <Box sx={{ px: 3, pb: 2, flexShrink: 0 }}>
        <Box
          onClick={() => setShowRecent((v) => !v)}
          sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", userSelect: "none" }}
        >
          {showRecent ? <ExpandLessIcon sx={{ fontSize: 18, color: "#888" }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: "#888" }} />}
          <Typography sx={{ fontSize: "0.82rem", color: "#666", fontWeight: 600 }}>
            Recently matched ({recentlyMatched.length})
          </Typography>
        </Box>
        <Collapse in={showRecent}>
          <Box sx={{ mt: 1, bgcolor: "#fff", borderRadius: 2, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", maxHeight: 260, overflowY: "auto" }}>
            {recentlyMatched.length === 0 ? (
              <Box sx={{ p: 2, color: "#aaa", fontSize: "0.82rem", textAlign: "center" }}>No recent matches</Box>
            ) : recentlyMatched.map((m) => (
              <Box key={m.AuditID} sx={{ display: "flex", alignItems: "center", px: 2, py: 1, borderBottom: "1px solid #f5f5f5", "&:last-child": { borderBottom: "none" } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#333" }}>
                      {AUD(m.TransAmount)}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: "#555", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 280 }}>
                      {m.TransText}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "0.72rem", color: "#888" }}>
                    {fmtDate(m.TransDate)} → {m.PayerName} #{m.PaymentID} · {m.MatchTier || m.Action} · {fmtDate(m.MatchedAt)}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => handleUnmatch(m.TransID)}
                  sx={{ fontSize: "0.72rem", color: "#c62828", ml: 1, flexShrink: 0 }}
                >
                  Unmatch
                </Button>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
      </>)}

      {/* ── Ignored view ─────────────────────────────────────────────── */}
      {view === "ignored" && (
        <Box sx={{ flex: 1, display: "flex", gap: 2, px: 3, py: 2, overflow: "hidden", minHeight: 0 }}>
          {/* Ignored payments */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", bgcolor: "#fff", borderRadius: 2, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
            <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: "1px solid #f0f0f0", flexShrink: 0, display: "flex", alignItems: "center", gap: 1 }}>
              <VisibilityOffIcon sx={{ fontSize: 18, color: Z_PURPLE }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: Z_PURPLE }}>
                Ignored Payments ({ignoredPayments.length})
              </Typography>
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {ignoredPayments.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center", color: "#aaa", fontSize: "0.85rem" }}>Nothing ignored</Box>
              ) : ignoredPayments.map((p) => (
                <Box key={p.PaymentID} sx={{ display: "flex", alignItems: "center", px: 2, py: 1.25, borderBottom: "1px solid #f5f5f5" }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.82rem", color: "#333", fontWeight: 600 }}>
                      {p.PayerName || <span style={{ color: "#aaa", fontStyle: "italic" }}>No payer linked</span>}
                      {" · "}{AUD(p.TotalPaymentValue)}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: "#888", mt: 0.25 }}>
                      {fmtDate(p.PayDate)}{p.ReferenceNumber ? ` · ${p.ReferenceNumber}` : ""}
                      {p.IgnoredBy ? ` · ignored by ${p.IgnoredBy}` : ""}{p.IgnoredAt ? ` on ${fmtDate(p.IgnoredAt)}` : ""}
                    </Typography>
                  </Box>
                  <Button size="small" startIcon={<UndoIcon sx={{ fontSize: 15 }} />} onClick={() => handleUnignorePayment(p.PaymentID)}
                    sx={{ fontSize: "0.72rem", color: Z_BLUE, ml: 1, flexShrink: 0 }}>
                    Restore
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Ignored bank credits */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", bgcolor: "#fff", borderRadius: 2, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
            <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: "1px solid #f0f0f0", flexShrink: 0, display: "flex", alignItems: "center", gap: 1 }}>
              <VisibilityOffIcon sx={{ fontSize: 18, color: Z_BLUE }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: Z_PURPLE }}>
                Ignored Bank Credits ({ignoredTrans.length})
              </Typography>
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {ignoredTrans.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center", color: "#aaa", fontSize: "0.85rem" }}>Nothing ignored</Box>
              ) : ignoredTrans.map((t) => (
                <Box key={t.TransID} sx={{ display: "flex", alignItems: "center", px: 2, py: 1.25, borderBottom: "1px solid #f5f5f5" }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.82rem", color: "#333", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {(t.TransText ?? "").replace(/^transfer from\s*/i, "")} · {AUD(t.TransAmount)}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: "#888", mt: 0.25 }}>
                      {fmtDate(t.TransDate)}
                      {t.IgnoredBy ? ` · ignored by ${t.IgnoredBy}` : ""}{t.IgnoredAt ? ` on ${fmtDate(t.IgnoredAt)}` : ""}
                    </Typography>
                  </Box>
                  <Button size="small" startIcon={<UndoIcon sx={{ fontSize: 15 }} />} onClick={() => handleUnignoreTrans(t.TransID)}
                    sx={{ fontSize: "0.72rem", color: Z_BLUE, ml: 1, flexShrink: 0 }}>
                    Restore
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Suggested matches review dialog ───────────────────────────── */}
      <SuggestedMatchesDialog
        open={suggestOpen}
        suggestions={suggested}
        confirming={confirmingSuggested}
        onClose={() => setSuggestOpen(false)}
        onConfirm={handleConfirmSuggested}
      />

      {/* ── Alias prompt dialog ───────────────────────────────────────── */}
      <Dialog open={Boolean(aliasPrompt)} onClose={() => handleSaveAlias(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "0.95rem", fontWeight: 700, pb: 1 }}>
          Save match hint?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", color: "#555", mb: 2 }}>
            Remember this text as a match hint for <strong>{aliasPrompt?.payerName}</strong>?
            Future transactions containing this text will auto-match.
          </Typography>
          <TextField
            fullWidth size="small" label="Alias text"
            value={editedAlias}
            onChange={(e) => setEditedAlias(e.target.value.toUpperCase())}
            inputProps={{ style: { fontFamily: "monospace", textTransform: "uppercase" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => handleSaveAlias(false)} sx={{ color: "#888", fontSize: "0.82rem" }}>No</Button>
          <Button
            onClick={() => handleSaveAlias(true)}
            disabled={!editedAlias.trim()}
            variant="contained"
            sx={{ bgcolor: Z_PURPLE, "&:hover": { bgcolor: "#2d1040" }, fontSize: "0.82rem" }}
          >
            Save "{editedAlias}"
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
